import { AfterViewInit, ChangeDetectionStrategy, Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { CCSyncDetails, CCSyncStatus } from '../../../../../../models/locations-models/cc-sync-details';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { DatePipe, NgClass } from '@angular/common';
import { selectCCSyncDetails, selectCCSyncStatus, selectCCSyncStatusIcon } from '../../../state/location-list-selectors';
import { Observable } from 'rxjs';
import { LocationListLocalStoreService } from '../../../location-list-local-store.service';
import { SyncActions } from '../../../state/location-list-actions';
import { LetDirective } from '@ngrx/component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Actions, ofType } from '@ngrx/effects';
import { map } from 'rxjs/operators';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressBar } from '@angular/material/progress-bar';
import { TranslateModule } from '@ngx-translate/core';
import { getElapsedTime } from '../restart-datasync/restart-datasync-utils';
import { isAfter } from 'date-fns';

@Component({
  selector: 'app-restart-cc-sync',
  templateUrl: './restart-cc-sync.component.html',
  styleUrls: ['./restart-cc-sync.component.scss',
    '../restart-datasync/restart-datasync.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIcon,
    MatTooltip,
    NgClass,
    LetDirective,
    MatProgressSpinner,
    DatePipe,
    MatButton,
    MatIconButton,
    MatMenu,
    MatProgressBar,
    TranslateModule,
    MatMenuTrigger,
  ],
})
export class RestartCcSyncComponent implements AfterViewInit {

  protected readonly loading$ = this.actions
      .pipe(
          ofType(SyncActions.restartCCSync,
              SyncActions.restartCCSyncSuccess,
              SyncActions.restartCCSyncError,
              SyncActions.getCCSyncDetails,
              SyncActions.getCCSyncDetailsSuccess,
              SyncActions.getCCSyncDetailsError),
          map((actions) => {
            switch (actions.type) {
              case SyncActions.restartCCSync.type:
              case SyncActions.getCCSyncDetails.type:
                return true;
              case SyncActions.restartCCSyncSuccess.type:
              case SyncActions.restartCCSyncError.type:
              case SyncActions.getCCSyncDetailsSuccess.type:
              case SyncActions.getCCSyncDetailsError.type:
                return false;
            }
          }));

  protected viewModel$: Observable<{
    ccSyncLoading: boolean,
    ccSyncIcon: { svgName: string, class: string },
    ccSyncStatus: CCSyncStatus,
    ccSyncDetails: CCSyncDetails,
    elapsedTime: string,
  }> = this.locationListLocalStore.select({
    ccSyncLoading: this.loading$,
    ccSyncIcon: this.store.select(selectCCSyncStatusIcon),
    ccSyncStatus: this.store.select(selectCCSyncStatus),
    ccSyncDetails: this.store.select(selectCCSyncDetails),
    elapsedTime: this.locationListLocalStore.select(this.store.select(selectCCSyncDetails),
        (details) => {
          if (details?.startDate &&
              (!details?.endDate || isAfter(new Date(details?.startDate), new Date(details?.endDate)))
          ) {
            // compare to start date to now if there is no end date yet, or if it's outdated
            const now = new Date().toISOString();
            return getElapsedTime(details.startDate, now);
          } else {
            return getElapsedTime(details?.startDate, details?.endDate);
          }
        }),
  });

  protected readonly ccSyncStatus = CCSyncStatus;

  constructor(private store: Store,
              private actions: Actions,
              private locationListLocalStore: LocationListLocalStoreService) {
  }

  ngAfterViewInit() {
    this.store.dispatch(SyncActions.getCCSyncDetails());
  }

  /**
   * Schedule a PLACES API data sync.
   */
  restartCCSync() {
    this.store.dispatch(SyncActions.restartCCSync());
  }

  /**
   * Get up-to-date status.
   */
  refreshCCSyncDetails() {
    this.store.dispatch(SyncActions.getCCSyncDetails());
  }

  /**
   * Users may schedule a sync if the status is STALE or FAILED
   * @param status
   */
  doesStatusAllowToScheduleSync(status: CCSyncStatus) {
    return status === CCSyncStatus.STALE || status === CCSyncStatus.FAILED;
  }
}
